import { motion } from "framer-motion";

const PARAGRAPHS = [
    "Working with you has been more than just working on a website. It's been an opportunity to collaborate with someone whose work and vision make the relationship meaningful.",
    "Thank you for trusting me and WebTechGuider with your website. Thank you for the collaboration, the conversations, the feedback, and the opportunity to contribute in my own way to something that matters to you.",
    "As a developer, I spend a lot of time thinking about pages, performance, design, and making sure everything works as it should. But relationships like ours are a reminder that behind every project are real people, trust, and the opportunity to create something worthwhile together.",
    "I'm genuinely grateful to have you as a client, and I appreciate the opportunity to work with you.",
    "On your birthday, I wish you a year filled with happiness, good health, peace of mind, meaningful relationships, exciting opportunities, and the satisfaction of seeing your dreams grow.",
    "May you keep inspiring people, creating beautiful experiences, and finding reasons to smile even on the busiest days.",
    "And please remember: today, you are officially prohibited from worrying about website updates, technical issues, or anything that can wait until tomorrow.",
    "Even the internet can survive one day without you.",
    "Happy birthday, Mary. Thank you for being part of the WebTechGuider journey.",
];

export default function Letter({ reduce }) {
    return (
        <section
            data-testid="letter-section"
            className="relative z-[1] bg-[#FBF7EE] px-6 py-24 text-[#2a1f0e] sm:py-32"
        >
            <div
                className="pointer-events-none absolute inset-0 opacity-[0.35]"
                aria-hidden="true"
                style={{
                    backgroundImage:
                        "radial-gradient(rgba(197,155,39,0.25) 1px, transparent 1px)",
                    backgroundSize: "26px 26px",
                }}
            />

            <div className="relative mx-auto max-w-2xl">
                <motion.div
                    className="text-center"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                    <p className="font-mono2 text-[11px] uppercase tracking-[0.32em] text-[#C59B27]">
                        Act IV — The Serious Moment
                    </p>
                    <h2 className="mt-4 font-display text-3xl font-semibold leading-snug sm:text-5xl">
                        Okay, one serious moment. <span className="italic text-[#C59B27]">No bugs, no jokes.</span>
                    </h2>
                </motion.div>

                <motion.article
                    data-testid="letter-card"
                    className="relative mt-12 rounded-sm bg-[#fffdf6] p-8 shadow-[0_30px_80px_-20px_rgba(90,64,20,0.35)] sm:p-12"
                    style={{
                        border: "1px solid rgba(197,155,39,0.35)",
                        boxShadow: "0 30px 80px -20px rgba(90,64,20,0.35), inset 0 0 0 1px rgba(197,155,39,0.15)",
                    }}
                    initial={reduce ? { opacity: 0 } : { opacity: 0, rotateX: -24, y: 60 }}
                    whileInView={reduce ? { opacity: 1 } : { opacity: 1, rotateX: 0, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                >
                    <div
                        className="absolute left-1/2 top-0 h-px w-2/3 -translate-x-1/2"
                        style={{ background: "linear-gradient(90deg, transparent, #C59B27, transparent)" }}
                        aria-hidden="true"
                    />

                    <p className="font-display text-3xl font-semibold italic text-[#7a5a10]">Dear Mary,</p>

                    <div className="mt-6 space-y-5">
                        {PARAGRAPHS.map((p, i) => (
                            <motion.p
                                key={i}
                                className="text-[15px] font-light leading-relaxed text-[#3a2c15] sm:text-base"
                                initial={{ opacity: 0, y: 14 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.7, delay: i * 0.06 }}
                            >
                                {p}
                            </motion.p>
                        ))}
                    </div>

                    <p className="mt-8 text-[15px] font-light text-[#3a2c15]">
                        With sincere appreciation and warm wishes,
                    </p>

                    <div className="mt-4 flex items-end justify-between">
                        <motion.svg
                            data-testid="jerry-signature"
                            viewBox="0 0 220 90"
                            className="h-20 w-48 text-[#7a5a10]"
                            fill="none"
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: "-40px" }}
                        >
                            <motion.path
                                d="M28 18 C 40 14, 46 24, 40 40 C 34 56, 20 74, 10 68 C 2 63, 12 50, 26 52 M 52 60 C 56 48, 64 44, 66 52 C 68 60, 58 66, 56 60 C 55 55, 66 50, 76 54 M 82 44 L 78 64 M 78 56 C 84 50, 92 50, 94 56 M 100 44 C 96 54, 94 62, 98 64 C 104 66, 110 52, 116 46 M 116 46 C 112 58, 110 64, 114 64 C 120 64, 128 50, 132 48 M 138 50 C 134 58, 132 66, 136 66 C 142 66, 148 52, 152 50 C 150 60, 148 74, 140 80 C 134 84, 128 80, 132 74"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                variants={{
                                    hidden: { pathLength: 0, opacity: 0 },
                                    show: {
                                        pathLength: 1,
                                        opacity: 1,
                                        transition: { duration: reduce ? 0.01 : 2.4, ease: "easeInOut", delay: 0.2 },
                                    },
                                }}
                            />
                            <motion.path
                                d="M20 82 C 70 76, 150 74, 200 78"
                                stroke="#C59B27"
                                strokeWidth="1.4"
                                strokeLinecap="round"
                                variants={{
                                    hidden: { pathLength: 0 },
                                    show: {
                                        pathLength: 1,
                                        transition: { duration: reduce ? 0.01 : 0.8, ease: "easeOut", delay: reduce ? 0 : 2.4 },
                                    },
                                }}
                            />
                        </motion.svg>
                        <div className="text-right">
                            <p className="font-display text-xl font-semibold italic">Jerry</p>
                            <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#7a5a10]/70">
                                WebTechGuider
                            </p>
                        </div>
                    </div>
                </motion.article>
            </div>
        </section>
    );
}
